import { db } from '../config/firebase';
import { 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  updateDoc, 
  onSnapshot,
  query,
  where,
  QuerySnapshot,
  DocumentData,
  serverTimestamp
} from 'firebase/firestore';
import { Seed } from '../types/idea-machine';

const seedsCollection = collection(db, 'seeds');
const unavailableSeedsCollection = collection(db, 'unavailableSeeds');

export const seedsService = {
  addSeed: async (prompt: string, category: string): Promise<void> => {
    try {
      await addDoc(seedsCollection, {
        prompt: prompt.trim(),
        category,
        timestamp: new Date(),
        active: true
      });
    } catch (error) {
      
      throw error;
    }
  },

  addUserSeed: async (userEmail: string, prompt: string): Promise<Seed> => {
    try {
      const docRef = await addDoc(seedsCollection, {
        prompt: prompt.trim(),
        category: 'User Created',
        timestamp: new Date(),
        isUserCreated: true,
        userEmail,
        active: true
      });
      
      return {
        id: docRef.id,
        prompt: prompt.trim(),
        category: 'User Created',
        timestamp: new Date(),
        isUserCreated: true,
        userEmail,
        active: true
      };
    } catch (error) {
      
      throw error;
    }
  },

  markSeedAsUnavailable: async (seedId: string, userEmail: string, reason: 'used' | 'deleted'): Promise<void> => {
    try {
      // Check if seed is already unavailable for this user
      const q = query(
        unavailableSeedsCollection, 
        where('seedId', '==', seedId),
        where('userEmail', '==', userEmail)
      );
      const snapshot = await getDocs(q);
      
      // If not already marked as unavailable, add it
      if (snapshot.empty) {
        await addDoc(unavailableSeedsCollection, {
          seedId,
          userEmail,
          reason,
          timestamp: serverTimestamp()
        });
      }
    } catch (error) {
      
      throw error;
    }
  },

  getUnavailableSeeds: async (userEmail: string): Promise<string[]> => {
    try {
      const q = query(unavailableSeedsCollection, where('userEmail', '==', userEmail));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data().seedId);
    } catch (error) {
      
      throw error;
    }
  },

  removeSeed: async (seedId: string): Promise<void> => {
    try {
      await deleteDoc(doc(seedsCollection, seedId));
    } catch (error) {
      
      throw error;
    }
  },

  deleteSeed: async (seedId: string, userEmail: string): Promise<void> => {
    try {
      // Only mark as unavailable for this user, don't actually delete the seed
      await seedsService.markSeedAsUnavailable(seedId, userEmail, 'deleted');
    } catch (error) {
      
      throw error;
    }
  },

  getSeeds: async (userEmail?: string): Promise<Seed[]> => {
    try {
      const querySnapshot = await getDocs(seedsCollection);
      const seeds = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          prompt: data.prompt || '',
          category: data.category || '',
          timestamp: data.timestamp?.toDate() || new Date(),
          isUserCreated: data.isUserCreated || false,
          userEmail: data.userEmail || '',
          active: data.active ?? true
        } as Seed;
      });

      if (userEmail) {
        const unavailableSeeds = await seedsService.getUnavailableSeeds(userEmail);
        return seeds.filter(seed => !unavailableSeeds.includes(seed.id));
      }

      return seeds;
    } catch (error) {
      
      throw error;
    }
  },

  getCategories: async (): Promise<string[]> => {
    try {
      const seeds = await seedsService.getSeeds();
      const categories = new Set<string>();
      
      seeds.forEach(seed => {
        if (seed.category) {
          categories.add(seed.category);
        }
      });

      return Array.from(categories);
    } catch (error) {
      
      throw error;
    }
  },

  updateSeed: async (seedId: string, data: Partial<Seed>): Promise<void> => {
    try {
      const seedRef = doc(seedsCollection, seedId);
      await updateDoc(seedRef, data);
    } catch (error) {
      
      throw error;
    }
  },

  updateSeeds: async (seeds: Seed[]): Promise<void> => {
    try {
      const updatePromises = seeds.map(seed => {
        if (!seed.id) return Promise.resolve();
        const seedRef = doc(seedsCollection, seed.id);
        return updateDoc(seedRef, {
          active: seed.active
        });
      });
      await Promise.all(updatePromises);
    } catch (error) {
      
      throw error;
    }
  },

  subscribeToSeeds: (callback: (seeds: Seed[]) => void, userEmail?: string) => {
    // Subscribe to both seeds and unavailable seeds collections
    const unsubscribeSeedsListener = onSnapshot(seedsCollection, async (seedsSnapshot: QuerySnapshot<DocumentData>) => {
      const seeds = seedsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          prompt: data.prompt || '',
          category: data.category || '',
          timestamp: data.timestamp?.toDate() || new Date(),
          isUserCreated: data.isUserCreated || false,
          userEmail: data.userEmail || '',
          active: data.active ?? true
        } as Seed;
      });

      if (userEmail) {
        // Get current unavailable seeds for the user
        const unavailableSeeds = await seedsService.getUnavailableSeeds(userEmail);
        callback(seeds.filter(seed => !unavailableSeeds.includes(seed.id)));
      } else {
        callback(seeds);
      }
    });

    // If userEmail is provided, also listen for changes to their unavailable seeds
    let unsubscribeUnavailableListener = () => {};
    if (userEmail) {
      const unavailableQuery = query(unavailableSeedsCollection, where('userEmail', '==', userEmail));
      unsubscribeUnavailableListener = onSnapshot(unavailableQuery, async () => {
        // When unavailable seeds change, re-fetch all seeds and filter
        const seeds = await seedsService.getSeeds(userEmail);
        callback(seeds);
      });
    }

    // Return a cleanup function that unsubscribes from both listeners
    return () => {
      unsubscribeSeedsListener();
      unsubscribeUnavailableListener();
    };
  },

  getRandomSeed: async (userEmail?: string): Promise<Seed | null> => {
    try {
      const seeds = await seedsService.getSeeds(userEmail);
      if (seeds.length === 0) return null;
      const randomIndex = Math.floor(Math.random() * seeds.length);
      return seeds[randomIndex];
    } catch (error) {
      console.error('Error getting random seed:', error);
      return null;
    }
  },

  getDailyChallengeSeed: async (): Promise<Seed | null> => {
    try {
      // Get all seeds (ignoring user availability for the daily challenge to ensure consistency)
      const seeds = await seedsService.getSeeds();
      if (seeds.length === 0) return null;

      // Create a deterministic hash from today's date
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      let hash = 0;
      for (let i = 0; i < today.length; i++) {
        const char = today.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      
      // Use positive hash to pick index
      const index = Math.abs(hash) % seeds.length;
      return seeds[index];
    } catch (error) {
      console.error('Error getting daily challenge seed:', error);
      return null;
    }
  }
};

// Export individual functions for the Admin Panel
export const { addSeed, removeSeed, getSeeds } = seedsService;
